import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { SectionHeading } from "@/components/Atoms";
import { ClosedLoopNotice } from "@/components/ClosedLoopNotice";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drama, Users, Clock, Radio, Ticket, Eye, ChevronRight, Plus, Crown, Lock, Coins } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function fmtDay(iso) {
  const d = new Date(iso);
  const today = new Date();
  const tom = new Date(); tom.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === tom.toDateString()) return "Tomorrow";
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}
function countdown(iso, now) {
  const s = Math.max(0, Math.floor((new Date(iso) - new Date(now)) / 1000));
  if (s < 60) return `in ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  return `in ${h}h ${m % 60}m`;
}

const ROLE_BADGE = {
  entrant: { label: "You're performing", cls: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/40" },
  visitor: { label: "You're judging", cls: "bg-cyan-500/20 text-cyan-200 border-cyan-400/40" },
  onlooker: { label: "Watching", cls: "bg-white/10 text-white/60 border-white/20" },
  spectator: { label: "Spectating", cls: "bg-violet-500/20 text-violet-200 border-violet-400/40" },
};

function HostShowDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "", minutes_from_now: 15, showcase_seconds: 10, entry_fee_step: 5, isProtected: false, password: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const create = useMutation({
    mutationFn: async () => (await api.post("/talentshow/create", {
      name: form.name.trim(),
      description: form.description.trim(),
      minutes_from_now: Math.max(2, Math.min(1440, parseInt(form.minutes_from_now) || 15)),
      showcase_seconds: Math.max(8, Math.min(30, parseInt(form.showcase_seconds) || 10)),
      entry_fee_step: Math.max(0, Math.min(50, parseInt(form.entry_fee_step) || 0)),
      password: form.isProtected ? (form.password || "").trim() : null,
    })).data,
    onSuccess: (d) => { toast.success("Your show is on the schedule! (−100C)"); onCreated?.(d); onClose(); },
    onError: (e) => toast.error(apiError(e, "Couldn't create the show")),
  });
  const pwInvalid = form.isProtected && form.password.trim().length < 1;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-md" data-testid="ts-host-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-white tracking-wide flex items-center gap-2"><Crown className="h-5 w-5 text-amber-300" /> Host a Show</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Create your own talent show with a custom name, description, start time, stage duration and entry fee. Hosting costs 100 credits.</DialogDescription>
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-amber-200 text-sm" data-testid="ts-host-cost">
            <Coins className="h-4 w-4 shrink-0" /> Hosting costs <span className="font-bold">100C</span>, charged when you create the show.
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">Show name</label>
            <Input data-testid="ts-host-name" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={60} placeholder="Midnight Holo Gala" className="bg-black/40 border-white/10 mt-1" />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">Description</label>
            <Textarea data-testid="ts-host-desc" value={form.description} onChange={(e) => set("description", e.target.value)} maxLength={240} rows={2} placeholder="Bring your shiniest cards…" className="bg-black/40 border-white/10 mt-1 resize-none" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">Starts in (min)</label>
              <Input data-testid="ts-host-start" type="number" min={2} max={1440} value={form.minutes_from_now} onChange={(e) => set("minutes_from_now", e.target.value)} className="bg-black/40 border-white/10 mt-1" />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">Stage (sec)</label>
              <Input data-testid="ts-host-secs" type="number" min={8} max={30} value={form.showcase_seconds} onChange={(e) => set("showcase_seconds", e.target.value)} className="bg-black/40 border-white/10 mt-1" />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">Fee step</label>
              <Input data-testid="ts-host-fee" type="number" min={0} max={50} value={form.entry_fee_step} onChange={(e) => set("entry_fee_step", e.target.value)} className="bg-black/40 border-white/10 mt-1" />
            </div>
          </div>
          {/* public vs password protected */}
          <div className="rounded-lg border border-white/10 p-3 space-y-2.5">
            <div className="flex gap-2">
              <button type="button" data-testid="ts-host-public" onClick={() => set("isProtected", false)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${!form.isProtected ? "border-cyan-400 bg-cyan-500/15 text-cyan-100" : "border-white/10 text-white/55 hover:border-white/25"}`}>
                <Eye className="h-4 w-4 inline mr-1.5" /> Public
              </button>
              <button type="button" data-testid="ts-host-private" onClick={() => set("isProtected", true)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.isProtected ? "border-amber-400 bg-amber-500/15 text-amber-100" : "border-white/10 text-white/55 hover:border-white/25"}`}>
                <Lock className="h-4 w-4 inline mr-1.5" /> Password
              </button>
            </div>
            {form.isProtected && (
              <Input data-testid="ts-host-password" value={form.password} onChange={(e) => set("password", e.target.value)} maxLength={40} placeholder="Set a password to join" className="bg-black/40 border-white/10" />
            )}
          </div>
          <p className="text-[10px] text-white/40">Entry fee stacks: 1st card costs the fee step, then ×2, ×3… Stage time is how long each card performs (the crowd can hype it for more).</p>
          <Button data-testid="ts-host-submit" disabled={form.name.trim().length < 2 || pwInvalid || create.isPending} onClick={() => create.mutate()} className="w-full bg-amber-400 text-black hover:bg-amber-300">
            {create.isPending ? "Creating…" : "Put it on the schedule (−100C)"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TalentShowPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [hostOpen, setHostOpen] = useState(false);
  const [category, setCategory] = useState("ongoing");
  const [roleFilter, setRoleFilter] = useState("all");
  const { data, isLoading } = useQuery({
    queryKey: ["ts-schedule"],
    queryFn: async () => (await api.get("/talentshow/schedule")).data,
    refetchInterval: 5000,
  });

  const shows = data?.shows || [];
  const now = data?.now;
  const ongoing = shows.filter(s => s.status === "live");
  const upcoming = shows.filter(s => s.status === "scheduled");
  const visibleShows = (category === "ongoing" ? ongoing : upcoming).filter(s => roleFilter === "all" || s.my_role === roleFilter);
  const grouped = visibleShows.reduce((acc, s) => {
    const day = fmtDay(s.scheduled_at);
    (acc[day] = acc[day] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-10" data-testid="talentshow-page">
      <SectionHeading
        eyebrow="The Stage"
        title="TALENT SHOW"
        description="Quarter-hourly shows where your cards strut their stuff before a live crowd. Enter a card to perform, or join the stands to judge — cheer with praise, or bury a card in shame."
      />

      <ClosedLoopNotice className="mb-6" testid="talentshow-closed-loop" />

      {user && (
        <div className="flex justify-end -mt-4 mb-6">
          <Button data-testid="ts-host-btn" onClick={() => setHostOpen(true)} className="bg-amber-400 text-black hover:bg-amber-300">
            <Crown className="h-4 w-4 mr-2" /> Host a Show
          </Button>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3 mb-8">
        {[
          { icon: Ticket, c: "#fb923c", t: "Enter to perform", d: "Stack cards onto the stage (5C, then +10, +15…). They're locked until the show ends." },
          { icon: Users, c: "#22d3ee", t: "Judge from the stands", d: "Bring a card to vote 👍/👎 (1C), shower Praise (paid to the owner), or throw Shame." },
          { icon: Drama, c: "#f472b6", t: "Shame sticks", d: "Every 1C of shame locks the card for 1 second — hidden & unusable until it lifts." },
        ].map((b) => (
          <div key={b.t} className="hud-pill rounded-2xl p-4" style={{ borderLeft: `2px solid ${b.c}66` }}>
            <b.icon className="h-5 w-5 mb-2" style={{ color: b.c }} />
            <div className="text-white font-semibold text-sm">{b.t}</div>
            <div className="text-white/45 text-[11px] mt-1 leading-relaxed">{b.d}</div>
          </div>
        ))}
      </div>

      {!user && (
        <div className="hud-pill rounded-2xl p-4 mb-6 text-sm text-amber-200/90" data-testid="ts-login-hint">
          <Button variant="link" className="px-1 text-amber-300" onClick={() => navigate("/login")}>Sign in</Button>
          to enter cards or join a show.
        </div>
      )}

      <Tabs value={category} onValueChange={setCategory} className="mb-6"><TabsList data-testid="ts-categories" className="flex w-full sm:w-fit h-auto flex-wrap gap-1 bg-black/30 border border-white/10 p-1">
        <TabsTrigger value="ongoing" data-testid="ts-category-ongoing" className="min-h-10 flex-1 text-white/60 data-[state=active]:bg-violet-600 data-[state=active]:text-white"><Radio className="h-4 w-4 mr-2" />Ongoing ({ongoing.length})</TabsTrigger>
        <TabsTrigger value="upcoming" data-testid="ts-category-upcoming" className="min-h-10 flex-1 text-white/60 data-[state=active]:bg-cyan-600 data-[state=active]:text-white"><Clock className="h-4 w-4 mr-2" />Upcoming ({upcoming.length})</TabsTrigger>
      </TabsList></Tabs>
      <div className="flex flex-wrap items-center gap-2 mb-5"><label htmlFor="ts-role-filter" data-testid="ts-role-filter-label" className="text-sm text-white/60">My participation</label><select id="ts-role-filter" data-testid="ts-role-filter" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="max-w-full rounded-md border border-white/15 bg-[hsl(222_22%_12%)] text-white text-sm px-3 py-2"><option value="all">All shows</option><option value="visitor" disabled={!user}>I'm judging</option><option value="entrant" disabled={!user}>I'm performing</option><option value="spectator" disabled={!user}>I'm spectating</option></select></div>
      {!isLoading && !visibleShows.length && <p data-testid="ts-ongoing-empty" className="text-white/50 py-8">{roleFilter === "all" ? `No ${category} shows right now.` : "No shows match this participation filter."}</p>}
      {isLoading ? (
        <div className="text-center text-white/40 py-12">Loading the schedule…</div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([day, list]) => (
            <div key={day}>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40 mb-3">{day}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((s, i) => {
                  const live = s.status === "live";
                  const badge = s.my_role && ROLE_BADGE[s.my_role];
                  return (
                    <motion.button
                      key={s.id}
                      data-testid={`ts-show-${s.id}`}
                      onClick={() => navigate(`/talentshow/${s.id}${live && !s.my_role ? "?spectate=1" : ""}`)}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i * 0.02, 0.3) }}
                      className={`hud-pill rounded-2xl p-4 text-left group hover:border-orange-400/40 transition-colors relative overflow-hidden ${live ? "border-rose-400/50" : ""}`}
                    >
                      {live && <div className="absolute inset-0 bg-rose-500/5 animate-pulse pointer-events-none" />}
                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-orange-300" />
                          <span className="font-display text-2xl text-white tracking-wide">{fmtTime(s.scheduled_at)}</span>
                        </div>
                        {live ? (
                          <span data-testid={`ts-spectate-${s.id}`} className="flex items-center gap-1.5 text-sm text-violet-200"><Eye className="h-4 w-4" />{s.my_role ? "Return to show" : "Spectate"}</span>
                        ) : (
                          <span className="text-[10px] font-mono text-white/40">{now && countdown(s.scheduled_at, now)}</span>
                        )}
                      </div>
                      {s.custom && (
                        <div className="relative mt-2">
                          <div className="flex items-center gap-1.5 text-amber-200 text-sm font-semibold truncate" data-testid="ts-show-name">
                            <Crown className="h-3.5 w-3.5 shrink-0 text-amber-300" /> {s.name}
                            {s.protected && <Lock className="h-3 w-3 shrink-0 text-white/50" data-testid="ts-show-locked" />}
                          </div>
                          {s.host_name && <div className="text-[10px] text-white/40">hosted by {s.host_name}{s.showcase_seconds ? ` · ${s.showcase_seconds}s stage` : ""}</div>}
                        </div>
                      )}
                      <div className="relative flex items-center gap-4 mt-3 text-xs text-white/55">
                        <span className="flex items-center gap-1"><Drama className="h-3.5 w-3.5 text-fuchsia-300" /> {s.entrant_count} performing</span>
                        <span data-testid={`ts-watching-${s.id}`} className="flex items-center gap-1"><Eye className="h-3.5 w-3.5 text-cyan-300" /> {s.visitor_count + (s.spectator_count || 0)} watching</span>
                      </div>
                      {badge && (
                        <div className={`relative mt-3 inline-block text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${badge.cls}`}>
                          {badge.label}{s.my_entry_count ? ` · ${s.my_entry_count}` : ""}
                        </div>
                      )}
                      <ChevronRight className="absolute bottom-4 right-4 h-4 w-4 text-white/30 group-hover:text-orange-300 group-hover:translate-x-0.5 transition-all" />
                    </motion.button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <HostShowDialog open={hostOpen} onClose={() => setHostOpen(false)}
        onCreated={(d) => { qc.invalidateQueries({ queryKey: ["ts-schedule"] }); if (d?.id) navigate(`/talentshow/${d.id}`); }} />
    </div>
  );
}
